// src/components/common/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. Receives a reset callback. */
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  padding: 2rem;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 0.75rem;
`;

const Message = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1.5rem;
  max-width: 480px;
  line-height: 1.6;
`;

const ResetButton = styled.button`
  padding: 0.5rem 1.25rem;
  background-color: #1a1a2e;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production you would send this to an error tracking service (e.g. Sentry)
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.handleReset);
      }

      return (
        <Wrapper role="alert">
          <Title>Something went wrong</Title>
          <Message>
            An unexpected error occurred in this section.{' '}
            {process.env.NODE_ENV === 'development' && this.state.error
              ? this.state.error.message
              : 'Please try refreshing the page or contact support if the problem persists.'}
          </Message>
          <ResetButton onClick={this.handleReset}>Try again</ResetButton>
        </Wrapper>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
